import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import Card from 'components/Card';
import DateTimePicker from 'components/DateTimePicker';
import {
  Toggle,
  FormField,
  FormLabel,
  SearchableSelect,
} from 'components/FormFields';
import Input from 'components/FormFields/Input';
import Textarea from 'components/FormFields/Textarea';

const Container = styled.div`
  width: 100%;
`;

const TopRow = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 1023px) {
    flex-direction: column;
    gap: 20px;
  }

  > * {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: auto;
  }

  > *:first-child {
    flex-basis: 200px;
  }

  > *:nth-child(2) {
    flex-basis: 350px;
  }

  @media (max-width: 1023px) {
    > * {
      flex-basis: auto !important;
      width: 100%;
    }
  }
`;

const ReasonRow = styled.div`
  width: 100%;
`;

// Helper function to get text based on adjustment type
const getAdjustmentText = (type, key) => {
  const texts = {
    credit: {
      details: 'Credit Details',
      amount: 'Amount',
      reason:
        'Brief description of why this credit is being issued (optional)...',
    },
    refund: {
      details: 'Refund Details',
      amount: 'Amount',
      reason:
        'Brief description of why this refund is being issued (optional)...',
    },
  };
  return texts[type]?.[key] || texts.credit[key];
};

const Step1CreditDetails = ({
  creditData,
  setCreditData,
  type = 'credit',
  isRegularAdmin = false,
  accounts = [],
  selectedAccountId = null,
  // eslint-disable-next-line no-empty-function
  onAccountChange = () => {},
}) => {
  const theme = useTheme();

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setCreditData((prev) => ({ ...prev, amount: value }));
    }
  };

  const handleReasonChange = (e) => {
    const value = e.target.value;
    setCreditData((prev) => ({ ...prev, reason: value }));
  };

  const handleExpirationChange = (date) => {
    setCreditData((prev) => ({ ...prev, expiration: date }));
  };

  const handleNotificationToggle = (e) => {
    const checked = e.target.checked;
    setCreditData((prev) => ({
      ...prev,
      sendMessage: checked,
      // Default to SMS when enabling notifications
      messageChannel:
        checked && !prev.messageChannel ? 'SMS' : prev.messageChannel,
    }));
  };

  return (
    <Container>
      <Card
        title={
          <>
            <svg
              width="16"
              height="16"
              fill={theme?.colors?.primary || '#fa4616'}
              viewBox="0 0 16 16"
              style={{ marginRight: '8px' }}
            >
              <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
            </svg>
            {getAdjustmentText(type, 'details')}
          </>
        }
      >
        <TopRow>
          {isRegularAdmin && (
            <div style={{ flexBasis: '300px' }}>
              <SearchableSelect
                label="Select Account"
                placeholder="-- Select an account --"
                options={accounts.map((account) => ({
                  label: account.business_dba || `Account ${account.id}`,
                  value: account.id,
                }))}
                value={selectedAccountId}
                onChange={(selected) =>
                  onAccountChange(selected ? selected.value : null)
                }
                required
              />
            </div>
          )}

          <Input
            label={getAdjustmentText(type, 'amount')}
            required
            placeholder="$0.00"
            value={creditData.amount}
            onChange={handleAmountChange}
            width="200px"
          />

          <FormField style={{ width: '350px' }}>
            <FormLabel>Set Expiration (optional)</FormLabel>
            <DateTimePicker
              value={creditData.expiration || null}
              onChange={handleExpirationChange}
              showTime={false}
              placeholder="No expiration"
              confirmText="Set Expiration"
              minDate={new Date()}
            />
          </FormField>

          <FormField style={{ width: 'auto' }}>
            <FormLabel>Notify Customer</FormLabel>
            <Toggle
              checked={creditData.sendMessage}
              onChange={handleNotificationToggle}
              checkedLabel="Notification enabled"
              uncheckedLabel="No notification"
            />
          </FormField>
        </TopRow>

        <ReasonRow>
          <Textarea
            label="Reason"
            rows={2}
            placeholder={getAdjustmentText(type, 'reason')}
            value={creditData.reason}
            onChange={handleReasonChange}
          />
        </ReasonRow>
      </Card>
    </Container>
  );
};

Step1CreditDetails.propTypes = {
  creditData: PropTypes.object.isRequired,
  setCreditData: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['credit', 'refund']),
  isRegularAdmin: PropTypes.bool,
  accounts: PropTypes.array,
  selectedAccountId: PropTypes.number,
  onAccountChange: PropTypes.func,
};

export default Step1CreditDetails;
